// src/lib/BaseCommentsPlugin.ts
import {
  createTSlatePlugin,
  TextApi
} from "@udecode/plate";

// src/lib/utils/getDraftCommentKey.ts
var getDraftCommentKey = () => `${BaseCommentsPlugin.key}_draft`;

// src/lib/utils/isCommentKey.ts
var isCommentKey = (key) => key.startsWith(`${BaseCommentsPlugin.key}_`);

// src/lib/utils/getCommentCount.ts
var getCommentCount = (node) => {
  let commentCount = 0;
  Object.keys(node).forEach((key) => {
    if (isCommentKey(key) && key !== getDraftCommentKey()) commentCount++;
  });
  return commentCount;
};

// src/lib/utils/getCommentKey.ts
var getCommentKey = (id) => `${BaseCommentsPlugin.key}_${id}`;

// src/lib/utils/getCommentKeyId.ts
var getCommentKeyId = (key) => key.replace(`${BaseCommentsPlugin.key}_`, "");

// src/lib/utils/getCommentKeys.ts
var getCommentKeys = (node) => {
  const keys = [];
  Object.keys(node).forEach((key) => {
    if (isCommentKey(key)) keys.push(key);
  });
  return keys;
};

// src/lib/utils/isCommentNodeById.ts
var isCommentNodeById = (node, id) => !!node[getCommentKey(id)];

// src/lib/utils/isCommentText.ts
var isCommentText = (node) => {
  return !!node[BaseCommentsPlugin.key];
};

// src/lib/withComments.ts
var withComments = ({
  editor,
  tf: { normalizeNode }
}) => ({
  transforms: {
    normalizeNode(entry) {
      const [node, path] = entry;
      if (node[BaseCommentsPlugin.key] && !node[getDraftCommentKey()] && getCommentCount(node) < 1) {
        editor.tf.unsetNodes(BaseCommentsPlugin.key, { at: path });
        return;
      }
      return normalizeNode(entry);
    }
  }
});

// src/lib/BaseCommentsPlugin.ts
var BaseCommentsPlugin = createTSlatePlugin({
  key: "comment",
  node: { isLeaf: true }
}).overrideEditor(withComments).extendApi(({ editor, type }) => ({
  has: (options) => {
    const { id } = options;
    const regex = new RegExp(`"${getCommentKey(id)}":true`);
    return regex.test(JSON.stringify(editor.children));
  },
  node: (options = {}) => {
    const { id, isDraft, ...rest } = options;
    return editor.api.node({
      ...rest,
      match: (n) => {
        if (isDraft) return n[type] && n[getDraftCommentKey()];
        return id ? isCommentNodeById(n, id) : n[type];
      }
    });
  },
  nodeId: (leaf) => {
    const ids = [];
    const keys = Object.keys(leaf);
    if (keys.includes(getDraftCommentKey())) return;
    keys.forEach((key) => {
      if (!isCommentKey(key) || key === getDraftCommentKey()) return;
      const id = getCommentKeyId(key);
      ids.push(id);
    });
    return ids.at(-1);
  },
  nodes: (options = {}) => {
    const { id, isDraft, ...rest } = options;
    return [
      ...editor.api.nodes({
        ...rest,
        match: (n) => {
          if (isDraft) return n[type] && n[getDraftCommentKey()];
          return id ? isCommentNodeById(n, id) : n[type];
        }
      })
    ];
  }
})).extendTransforms(
  ({ api, editor, tf, type }) => ({
    removeMark: () => {
      const nodeEntry = api.comment.node();
      if (!nodeEntry) return;
      const keys = getCommentKeys(nodeEntry[0]);
      editor.tf.withoutNormalizing(() => {
        keys.forEach((key) => {
          editor.tf.removeMark(key);
        });
        editor.tf.removeMark(BaseCommentsPlugin.key);
      });
    },
    setDraft: (options = {}) => {
      tf.setNodes(
        {
          [getDraftCommentKey()]: true,
          [type]: true
        },
        { match: TextApi.isText, split: true, ...options }
      );
    },
    unsetMark: (options) => {
      const { id } = options;
      const nodes = api.comment.nodes({ id, at: [] });
      if (!nodes) return;
      nodes.forEach(([node]) => {
        const isOverlapping = getCommentCount(node) > 1;
        let unsetKeys = [];
        if (isOverlapping) {
          unsetKeys = [getDraftCommentKey(), getCommentKey(id)];
        } else {
          unsetKeys = [
            BaseCommentsPlugin.key,
            getDraftCommentKey(),
            getCommentKey(id)
          ];
        }
        editor.tf.unsetNodes(unsetKeys, {
          at: [],
          match: (n) => n === node
        });
      });
    }
  })
);
export {
  BaseCommentsPlugin,
  getCommentCount,
  getCommentKey,
  getCommentKeyId,
  getCommentKeys,
  getDraftCommentKey,
  isCommentKey,
  isCommentNodeById,
  isCommentText,
  withComments
};
//# sourceMappingURL=index.mjs.map